<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import SearchAPI from 'dashboard/api/search';
import { useCamelCase } from 'dashboard/composables/useTransformKeys';
import SearchInput from 'dashboard/modules/search/components/SearchInput.vue';
import SearchResultMessagesList from 'dashboard/modules/search/components/SearchResultMessagesList.vue';
import SearchResultConversationsList from 'dashboard/modules/search/components/SearchResultConversationsList.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const PER_PAGE = 15;

const props = defineProps({
  filters: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

const query = ref('');
const conversations = ref([]);
const messages = ref([]);
const conversationPage = ref(1);
const messagePage = ref(1);
const isFetchingConversations = ref(false);
const isFetchingMessages = ref(false);
const hasCompletedSearch = ref(false);

const normalizedQuery = computed(() => query.value.trim());
const hasQuery = computed(() => Boolean(normalizedQuery.value));

const assigneeId = computed(() => {
  if (!props.filters.agent?.startsWith('agent:')) {
    return undefined;
  }

  return Number(props.filters.agent.split(':')[1]);
});

const baseParams = computed(() => ({
  since: props.filters.dateRange?.from || undefined,
  until: props.filters.dateRange?.to || undefined,
  inboxId: props.filters.inboxId || undefined,
  assigneeId: assigneeId.value,
  status: props.filters.status !== 'all' ? props.filters.status : undefined,
  monitorMode: true,
}));

const hasActiveMonitorFilters = computed(
  () =>
    Boolean(baseParams.value.since) ||
    Boolean(baseParams.value.until) ||
    Boolean(baseParams.value.inboxId) ||
    Boolean(baseParams.value.assigneeId) ||
    Boolean(baseParams.value.status)
);

const showConversationLoadMore = computed(
  () =>
    hasQuery.value &&
    !isFetchingConversations.value &&
    conversations.value.length > 0 &&
    conversations.value.length === conversationPage.value * PER_PAGE
);

const showMessageLoadMore = computed(
  () =>
    hasQuery.value &&
    !isFetchingMessages.value &&
    messages.value.length > 0 &&
    messages.value.length === messagePage.value * PER_PAGE
);

const showEmptyState = computed(
  () =>
    hasQuery.value &&
    hasCompletedSearch.value &&
    !isFetchingConversations.value &&
    !isFetchingMessages.value &&
    conversations.value.length === 0 &&
    messages.value.length === 0
);

const normalizeRecords = records =>
  records.map(record => useCamelCase(record, { deep: true }));

const clearResults = () => {
  conversations.value = [];
  messages.value = [];
  conversationPage.value = 1;
  messagePage.value = 1;
  hasCompletedSearch.value = false;
};

const fetchConversations = async ({ reset = false } = {}) => {
  isFetchingConversations.value = true;

  try {
    const {
      data: {
        payload: { conversations: records = [] },
      },
    } = await SearchAPI.conversations({
      q: normalizedQuery.value,
      page: conversationPage.value,
      ...baseParams.value,
    });

    const normalized = normalizeRecords(records);
    conversations.value = reset
      ? normalized
      : [...conversations.value, ...normalized];
  } catch {
    if (reset) {
      conversations.value = [];
    }
  } finally {
    isFetchingConversations.value = false;
  }
};

const fetchMessages = async ({ reset = false } = {}) => {
  isFetchingMessages.value = true;

  try {
    const {
      data: {
        payload: { messages: records = [] },
      },
    } = await SearchAPI.messages({
      q: normalizedQuery.value,
      page: messagePage.value,
      ...baseParams.value,
    });

    const normalized = normalizeRecords(records);
    messages.value = reset ? normalized : [...messages.value, ...normalized];
  } catch {
    if (reset) {
      messages.value = [];
    }
  } finally {
    isFetchingMessages.value = false;
  }
};

const executeSearch = async ({ reset = true } = {}) => {
  if (!hasQuery.value) {
    clearResults();
    return;
  }

  if (reset) {
    clearResults();
  }

  await Promise.all([
    fetchConversations({ reset }),
    fetchMessages({ reset }),
  ]);

  hasCompletedSearch.value = true;
};

const onSearch = async value => {
  query.value = value || '';
  await executeSearch({ reset: true });
};

const loadMoreConversations = async () => {
  if (!showConversationLoadMore.value) return;

  conversationPage.value += 1;
  await fetchConversations({ reset: false });
};

const loadMoreMessages = async () => {
  if (!showMessageLoadMore.value) return;

  messagePage.value += 1;
  await fetchMessages({ reset: false });
};

watch(
  () => props.filters,
  async () => {
    if (!hasQuery.value) return;
    await executeSearch({ reset: true });
  },
  { deep: true }
);
</script>

<template>
  <div class="chatmigo-monitor-panel flex h-full flex-col">
    <div class="chatmigo-monitor-toolbar px-3 py-3">
      <SearchInput
        v-model="query"
        @search="onSearch"
        @select-recent-search="onSearch"
      />
      <p class="mt-3 mb-0 text-xs leading-5 text-n-slate-10">
        {{
          hasActiveMonitorFilters
            ? t('MONITOR_PAGE.SEARCH.FILTERED_HINT')
            : t('MONITOR_PAGE.SEARCH.DEFAULT_HINT')
        }}
      </p>
    </div>

    <div class="flex-1 overflow-y-auto px-3 py-4">
      <div
        v-if="!hasQuery"
        class="chatmigo-empty-state flex flex-col items-center justify-center px-6 py-10 text-center"
      >
        <fluent-icon icon="search" size="24" class="text-n-slate-11" />
        <p class="m-2 text-n-slate-11">
          {{ t('MONITOR_PAGE.SEARCH.EMPTY_DEFAULT') }}
        </p>
      </div>

      <template v-else>
        <div
          v-if="conversations.length"
          class="mb-2 flex items-center gap-2 px-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-n-slate-10"
        >
          <fluent-icon icon="chat" size="12" />
          <span>{{ t('MONITOR_PAGE.VIEWS.CONVERSATIONS') }}</span>
        </div>
        <SearchResultConversationsList
          :conversations="conversations"
          :query="query"
          :is-fetching="isFetchingConversations"
          :show-empty-state="false"
          monitor-mode
        />

        <div v-if="showConversationLoadMore" class="flex justify-center mb-5">
          <NextButton
            :label="t('MONITOR_PAGE.SEARCH.LOAD_MORE_CONVERSATIONS')"
            icon="i-lucide-cloud-download"
            slate
            sm
            faded
            @click="loadMoreConversations"
          />
        </div>

        <div
          v-if="messages.length"
          class="mb-2 flex items-center gap-2 px-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-n-slate-10"
        >
          <fluent-icon icon="document" size="12" />
          <span>{{ t('SEARCH.SECTION.MESSAGES') }}</span>
        </div>
        <SearchResultMessagesList
          :messages="messages"
          :query="query"
          :is-fetching="isFetchingMessages"
          :show-empty-state="false"
          monitor-mode
        />

        <div v-if="showMessageLoadMore" class="flex justify-center">
          <NextButton
            :label="t('MONITOR_PAGE.SEARCH.LOAD_MORE_MESSAGES')"
            icon="i-lucide-cloud-download"
            slate
            sm
            faded
            @click="loadMoreMessages"
          />
        </div>

        <div
          v-if="showEmptyState"
          class="chatmigo-empty-state flex flex-col items-center justify-center px-6 py-10 text-center"
        >
          <fluent-icon icon="search" size="24" class="text-n-slate-11" />
          <p class="m-2 text-n-slate-11">
            {{ t('MONITOR_PAGE.SEARCH.EMPTY_RESULTS', { query }) }}
          </p>
        </div>
      </template>
    </div>
  </div>
</template>
